import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import Section1 from "./Section1";
import Section2 from "./Section2";
import { fetchSurveyStructure, submitContact, submitSurvey } from "./surveyApi";
import { calculateScores } from "./surveyScoring";

const COMPANY_SIZES = [
  { value: "A", label: "1-20" },
  { value: "B", label: "21-100" },
  { value: "C", label: "101-500" },
  { value: "D", label: "500-2,500" },
  { value: "E", label: "2,500+" },
];

const EMPTY_CONTACT = {
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  companySize: "",
};

const AIReadinessSurvey = () => {
  // view: 'landing' | 'survey' | 'submitting' | 'complete'
  const [view, setView] = useState("landing");
  const [showModal, setShowModal] = useState(false);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [completedContact, setCompletedContact] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);

  // Survey structure fetched from API (or static fallback)
  const [surveyStructure, setSurveyStructure] = useState(null);
  const [structureLoading, setStructureLoading] = useState(true);
  const [structureError, setStructureError] = useState(null);

  // Modal async states
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Fetch survey questions/structure on mount
  useEffect(() => {
    fetchSurveyStructure()
      .then(setSurveyStructure)
      .catch((err) => setStructureError(err.message))
      .finally(() => setStructureLoading(false));
  }, []);

  const setField = (key, val) => {
    setContact((prev) => ({ ...prev, [key]: val }));
    setFieldErrors((prev) => ({ ...prev, [key]: false }));
    if (modalError) setModalError(null);
  };

  const validateContact = () => {
    const errs = {};
    if (!contact.firstName.trim()) errs.firstName = true;
    if (!contact.lastName.trim()) errs.lastName = true;
    if (!contact.company.trim()) errs.company = true;
    if (
      !contact.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)
    )
      errs.email = true;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!validateContact()) return;
    setModalSubmitting(true);
    setModalError(null);
    try {
      const { submissionId: sid } = await submitContact({
        firstName: contact.firstName,
        lastName: contact.lastName,
        company: contact.company,
        email: contact.email,
        companySize: contact.companySize || null,
      });
      setSubmissionId(sid);
      setCompletedContact({ ...contact });
      setShowModal(false);
      setView("survey");
    } catch (err) {
      // setShowModal(false);
      // setView("survey");
      setModalError("Something went wrong. Please try again.");
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleSurveyComplete = async (data) => {
    setView("submitting");
    const deptQ = surveyStructure?.sections?.A?.find((q) => q.isDepartmentSelect);
    const deptOptionId = deptQ ? data.answers?.[deptQ.id] : null;
    const deptKey = deptOptionId
      ? (surveyStructure?.departmentKeys?.[deptOptionId] || null)
      : null;

    const scoring = calculateScores(data.answers, surveyStructure);

    const payload = {
      submissionId,
      contact: data.contactInfo,
      department: deptKey,
      answers: data.answers,
      otherIndustry: data.otherIndustry || null,
      completedAt: new Date().toISOString(),
      scoring,
    };
    try {
      await submitSurvey(payload);
    } catch (err) {
      console.error("Survey submission error:", err);
    }
    setView("complete");
  };

  // Full-page error if structure could not be loaded
  if (structureError) {
    return (
      <main className="survey-main">
        <div className="survey-structure-error">
          <p>Failed to load survey. Please refresh the page and try again.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="survey-main">
      {/* ── Landing ── */}
      {view === "landing" && (
        <Section1
          onTakeSurvey={() => setShowModal(true)}
          isLoading={structureLoading}
        />
      )}

      {/* ── Survey form ── */}
      {view === "survey" && (
        <Section2
          contactInfo={completedContact}
          onComplete={handleSurveyComplete}
          surveyStructure={surveyStructure}
        />
      )}

      {/* ── Submitting ── */}
      {view === "submitting" && (
        <div className="survey-submitting">
          <div className="survey-submitting-inner">
            <div className="submit-spinner" />
            <p>Submitting your survey...</p>
          </div>
        </div>
      )}

      {/* ── Complete ── */}
      {view === "complete" && (
        <div className="survey-complete">
          <div className="survey-complete-inner">
            <div className="complete-icon">
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2>
              Survey <span className="yellow-txt">Complete!</span>
            </h2>
            <p>
              Thank you, <strong>{completedContact?.firstName}</strong>! Your
              personalized <strong>AI Readiness Report</strong> — including your
              overall readiness score, maturity tier, and department-specific
              breakdown — has been sent to{" "}
              <strong>{completedContact?.email}</strong>.
            </p>
            <Link to="/" className="complete-back-btn">
              Back to Home
            </Link>
          </div>
        </div>
      )}

      {/* ── Contact modal ── */}
      {showModal && (
        <div
          className="survey-modal-overlay"
          onClick={(e) =>
            e.target === e.currentTarget && setShowModal(false)
          }
        >
          <div className="survey-modal">
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <h2 className="modal-title">
              Let's get <span className="yellow-txt">started.</span>
            </h2>
            <p className="modal-subtitle">
              We'll email your personalized AI Readiness Report to the address
              below.
            </p>

            {modalError && (
              <p className="modal-api-error">{modalError}</p>
            )}

            <form onSubmit={handleModalSubmit} noValidate>
              <div className="modal-form-row">
                <div className="modal-form-group">
                  <label htmlFor="m-fname">First Name *</label>
                  <input
                    id="m-fname"
                    type="text"
                    placeholder="John"
                    value={contact.firstName}
                    onChange={(e) => setField("firstName", e.target.value)}
                  />
                  {fieldErrors.firstName && (
                    <span className="field-error">Required</span>
                  )}
                </div>
                <div className="modal-form-group">
                  <label htmlFor="m-lname">Last Name *</label>
                  <input
                    id="m-lname"
                    type="text"
                    placeholder="Smith"
                    value={contact.lastName}
                    onChange={(e) => setField("lastName", e.target.value)}
                  />
                  {fieldErrors.lastName && (
                    <span className="field-error">Required</span>
                  )}
                </div>
              </div>

              <div className="modal-form-group">
                <label htmlFor="m-company">Company Name *</label>
                <input
                  id="m-company"
                  type="text"
                  placeholder="Acme Corp"
                  value={contact.company}
                  onChange={(e) => setField("company", e.target.value)}
                />
                {fieldErrors.company && (
                  <span className="field-error">Required</span>
                )}
              </div>

              <div className="modal-form-group">
                <label htmlFor="m-email">Work Email *</label>
                <input
                  id="m-email"
                  type="email"
                  placeholder="john@acmecorp.com"
                  value={contact.email}
                  onChange={(e) => setField("email", e.target.value)}
                />
                {fieldErrors.email && (
                  <span className="field-error">Enter a valid work email</span>
                )}
              </div>

              <div className="modal-form-group">
                <label htmlFor="m-size">Company Size</label>
                <select
                  id="m-size"
                  value={contact.companySize}
                  onChange={(e) => setField("companySize", e.target.value)}
                >
                  <option value="">Select company size</option>
                  {COMPANY_SIZES.map((s) => (
                    <option key={s.value} value={s.label}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="modal-submit-btn"
                disabled={modalSubmitting}
              >
                {modalSubmitting ? "Starting…" : "Start Survey →"}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default AIReadinessSurvey;
